import React, { useState } from 'react';
import { Card, Tag, Space, Button, Typography, Divider, Alert } from 'antd';
import { LinkOutlined, SaveOutlined, GlobalOutlined, TranslationOutlined, ExpandAltOutlined, PrinterOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { useLanguage } from '../context/LanguageContext';
import './MedlinePlusContentCard.css';

const { Text, Paragraph, Title } = Typography;

/**
 * Component for displaying MedlinePlus Connect content with Vietnamese translation support
 * Handles proper medical terminology formatting and source attribution
 * Enhanced with print-friendly display and expandable content view
 */
const MedlinePlusContentCard = ({
  result,
  onSave,
  loading = false,
  showSaveButton = true,
  style = {},
  defaultExpanded = false
}) => {
  const { currentLanguage } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  if (!result) {
    return null;
  }

  const isVietnamese = currentLanguage === 'vi';
  const hasVietnameseContent = result.vietnameseContent &&
    (result.vietnameseContent.title || result.vietnameseContent.summary);

  // Determine which content to display - prefer enhanced content if available
  const hasEnhancedContent = result.enhancedContent && result.enhancedContent.isEnhanced;
  const content = isVietnamese && hasVietnameseContent
    ? result.vietnameseContent
    : hasEnhancedContent
      ? result.enhancedContent
      : result.originalContent;

  // Get the appropriate related links
  const relatedLinks = isVietnamese && result.vietnameseContent?.relatedLinks
    ? result.vietnameseContent.relatedLinks
    : content?.relatedLinks || [];

  // Handle print functionality
  const handlePrint = () => {
    // Create a new window with just the health information content
    const printWindow = window.open('', '_blank');
    const printContent = generatePrintContent();

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  // Generate print-friendly HTML content
  const generatePrintContent = () => {
    const currentDate = new Date().toLocaleDateString();

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${content.title} - Health Information</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              line-height: 1.6;
              margin: 20px;
              color: #333;
            }
            .header {
              border-bottom: 2px solid #1890ff;
              padding-bottom: 10px;
              margin-bottom: 20px;
            }
            .title {
              color: #1890ff;
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 10px;
            }
            .code-info {
              background: #f5f5f5;
              padding: 10px;
              border-radius: 4px;
              margin-bottom: 15px;
            }
            .content {
              margin-bottom: 20px;
            }
            .section {
              margin-bottom: 15px;
            }
            .section-title {
              font-weight: bold;
              color: #1890ff;
              margin-bottom: 5px;
            }
            .related-links {
              margin-top: 15px;
            }
            .related-links ul {
              list-style-type: disc;
              margin-left: 20px;
            }
            .footer {
              border-top: 1px solid #ddd;
              padding-top: 15px;
              margin-top: 30px;
              font-size: 12px;
              color: #666;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">${content.title}</div>
            <div class="code-info">
              <strong>Medical Code:</strong> ${result.codeSystem} ${result.code}
              ${result.codeName ? `<br><strong>Code Name:</strong> ${result.codeName}` : ''}
            </div>
          </div>

          <div class="content">
            ${content.summary ? `
              <div class="section">
                <div class="section-title">Summary</div>
                <div>${content.summary}</div>
              </div>
            ` : ''}

            ${content.alsoCalled ? `
              <div class="section">
                <div class="section-title">Also Called</div>
                <div>${content.alsoCalled}</div>
              </div>
            ` : ''}

            ${hasEnhancedContent && content.sections && content.sections.length > 0 ? `
              <div class="section">
                <div class="section-title">Detailed Information</div>
                ${content.sections.map(section => `
                  <div style="margin-bottom: 15px; padding: 10px; background: #f8f9fa; border-left: 3px solid #1890ff;">
                    <div style="font-weight: bold; color: #1890ff; margin-bottom: 5px;">${section.title}</div>
                    ${section.content ? `<div style="font-size: 14px; line-height: 1.5;">${section.content}</div>` : ''}
                    ${section.link ? `<div style="margin-top: 5px;"><a href="${section.link}" target="_blank" style="font-size: 12px;">Learn more</a></div>` : ''}
                  </div>
                `).join('')}
              </div>
            ` : ''}

            ${relatedLinks.length > 0 ? `
              <div class="section related-links">
                <div class="section-title">Related Links</div>
                <ul>
                  ${relatedLinks.map(link => `
                    <li><a href="${link.url}" target="_blank">${isVietnamese && link.title_vn ? link.title_vn : link.title}</a></li>
                  `).join('')}
                </ul>
              </div>
            ` : ''}
          </div>

          <div class="footer">
            <div><strong>Source:</strong> ${isVietnamese ? 'MedlinePlus, Thư viện Y học Quốc gia Hoa Kỳ' : 'MedlinePlus, U.S. National Library of Medicine'}</div>
            <div><strong>URL:</strong> <a href="${content.url}" target="_blank">${content.url}</a></div>
            ${result.originalContent?.attribution ? `<div><strong>Attribution:</strong> ${result.originalContent.attribution}</div>` : ''}
            <div><strong>Retrieved:</strong> ${result.fetchDate ? new Date(result.fetchDate).toLocaleDateString() : currentDate}</div>
            <div><strong>Printed:</strong> ${currentDate}</div>
          </div>
        </body>
      </html>
    `;
  };

  return (
    <Card
      className="medlineplus-content-card"
      style={{ marginBottom: 16, ...style }}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, marginRight: 16 }}>
            <Title level={4} style={{ margin: 0, marginBottom: 8 }}>
              {content.title}
            </Title>
            <Space wrap>
              <Tag color="blue" icon={<GlobalOutlined />}>
                {result.codeSystem}: {result.code}
              </Tag>
              {result.codeName && (
                <Tag color="geekblue">
                  {result.codeName}
                </Tag>
              )}
              {isVietnamese && hasVietnameseContent && (
                <Tag color="green" icon={<TranslationOutlined />}>
                  Translated to Vietnamese
                </Tag>
              )}
              {isVietnamese && !hasVietnameseContent && (
                <Tag color="orange">
                  English Only
                </Tag>
              )}
              {hasEnhancedContent && (
                <Tag color="purple" icon={<ExpandAltOutlined />}>
                  Enhanced Content
                </Tag>
              )}
            </Space>
          </div>
        </div>
      }
      extra={
        <Space direction="vertical" size="small" className="medlineplus-action-buttons">
          <Space size="small">
            <Button
              type="default"
              size="small"
              onClick={() => setIsExpanded(!isExpanded)}
              icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
              title={isExpanded ? "Collapse" : "Expand"}
            >
              {isExpanded ? "Collapse" : "Expand"}
            </Button>
            <Button
              type="default"
              size="small"
              onClick={handlePrint}
              icon={<PrinterOutlined />}
              title="Print Health Information"
              className="medlineplus-print-button"
            >
              Print
            </Button>
          </Space>
          {showSaveButton && (
            <Button
              type="primary"
              size="small"
              onClick={() => onSave && onSave(result)}
              loading={loading}
              icon={<SaveOutlined />}
            >
              Save to Library
            </Button>
          )}
          <Button
            type="default"
            size="small"
            href={content.url}
            target="_blank"
            rel="noopener noreferrer"
            icon={<LinkOutlined />}
          >
            View Original
          </Button>
        </Space>
      }
    >
      {/* Compact Summary View */}
      {!isExpanded && content.summary && (
        <div style={{ marginBottom: 16 }}>
          <Paragraph>
            {content.summary.length > 300
              ? `${content.summary.substring(0, 300)}...`
              : content.summary
            }
          </Paragraph>
          {content.summary.length > 300 && (
            <Button
              type="link"
              size="small"
              onClick={() => setIsExpanded(true)}
              icon={<ExpandAltOutlined />}
              style={{ padding: 0, height: 'auto' }}
            >
              Show full content
            </Button>
          )}
        </div>
      )}

      {/* Expanded Content View */}
      {isExpanded && (
        <div className="medlineplus-expanded-content medlineplus-expand-transition">
          {/* Full Summary Content */}
          {content.summary && (
            <div style={{ marginBottom: 16 }}>
              <div className="medlineplus-section-title">
                {isVietnamese ? 'Tóm tắt' : 'Summary'}
              </div>
              <div className="medlineplus-content-text">
                {content.summary}
              </div>
            </div>
          )}

          {/* Also Called Terms */}
          {content.alsoCalled && (
            <div style={{ marginBottom: 16 }}>
              <div className="medlineplus-section-title">
                {isVietnamese ? 'Còn được gọi là' : 'Also Called'}
              </div>
              <div className="medlineplus-content-text">{content.alsoCalled}</div>
            </div>
          )}

          {/* Structured Content Sections (Enhanced Content) */}
          {hasEnhancedContent && content.sections && content.sections.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div className="medlineplus-section-title">
                {isVietnamese ? 'Nội dung chi tiết' : 'Detailed Content'}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px', marginTop: 12 }}>
                {content.sections.map((section, sectionIndex) => (
                  <div
                    key={sectionIndex}
                    style={{
                      background: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      borderRadius: '6px',
                      padding: '12px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#e9ecef';
                      e.target.style.borderColor = '#1890ff';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#f8f9fa';
                      e.target.style.borderColor = '#e9ecef';
                    }}
                  >
                    <div style={{
                      fontWeight: 'bold',
                      color: '#1890ff',
                      marginBottom: '6px',
                      fontSize: '14px'
                    }}>
                      {section.title}
                    </div>
                    {section.content && (
                      <div style={{
                        fontSize: '13px',
                        color: '#666',
                        lineHeight: '1.4'
                      }}>
                        {section.content.length > 150
                          ? `${section.content.substring(0, 150)}...`
                          : section.content
                        }
                      </div>
                    )}
                    {section.link && (
                      <div style={{ marginTop: '8px' }}>
                        <a
                          href={section.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontSize: '12px',
                            color: '#1890ff',
                            textDecoration: 'none'
                          }}
                        >
                          Learn more →
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Related Links */}
          {relatedLinks && relatedLinks.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div className="medlineplus-section-title">
                {isVietnamese ? 'Liên kết liên quan' : 'Related Links'}
              </div>
              <ul className="medlineplus-related-links">
                {relatedLinks.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {isVietnamese && link.title_vn ? link.title_vn : link.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Additional Information Notice */}
          <Alert
            message={isVietnamese ? 'Thông tin chi tiết' : 'For Complete Information'}
            description={
              <span>
                {isVietnamese
                  ? 'Để có thông tin đầy đủ và cập nhật nhất, vui lòng truy cập trang web gốc của MedlinePlus.'
                  : 'For the most complete and up-to-date information, please visit the original MedlinePlus page.'
                }
                <br />
                <a
                  href={content.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#1890ff', fontWeight: 'bold' }}
                >
                  {content.url}
                </a>
              </span>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        </div>
      )}

      {/* Translation Status Alert */}
      {isVietnamese && !hasVietnameseContent && (
        <Alert
          message="Nội dung chỉ có sẵn bằng tiếng Anh"
          description="Thông tin y tế này hiện chỉ có sẵn bằng tiếng Anh từ MedlinePlus. Chúng tôi đang làm việc để cung cấp bản dịch tiếng Việt."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Source Attribution */}
      <Divider style={{ margin: '16px 0 8px 0' }} />
      <div style={{ fontSize: '12px', color: '#666' }}>
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div>
            <Text italic>
              {isVietnamese 
                ? 'Nguồn: MedlinePlus, Thư viện Y học Quốc gia Hoa Kỳ' 
                : 'Source: MedlinePlus, U.S. National Library of Medicine'
              }
            </Text>
          </div>
          
          {result.originalContent?.attribution && (
            <div>
              <Text italic style={{ fontSize: '11px' }}>
                {result.originalContent.attribution}
              </Text>
            </div>
          )}
          
          <div>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              {isVietnamese 
                ? `Lấy từ: ${result.source || 'API'} • Mã: ${result.codeSystem} ${result.code}` 
                : `Retrieved from: ${result.source || 'API'} • Code: ${result.codeSystem} ${result.code}`
              }
            </Text>
          </div>

          {result.fetchDate && (
            <div>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {isVietnamese 
                  ? `Cập nhật: ${new Date(result.fetchDate).toLocaleDateString('vi-VN')}` 
                  : `Updated: ${new Date(result.fetchDate).toLocaleDateString()}`
                }
              </Text>
            </div>
          )}
        </Space>
      </div>
    </Card>
  );
};

export default MedlinePlusContentCard;
